import { createContext } from 'react';

import { STORAGE_KEYS } from '@config';

const defaultContextValue = {} as OptionsContextType;

type OptionsState = {
  appInfo: AppInfo;
  organizations: OrganizationWithRole[];
  activeOrganization: OrganizationWithRole | null;
  activeProject: Project | null;
  activeFlow: Flow | 'all' | null;
};

type OptionsAction = {
  type: string;
  appInfo?: AppInfo;
  organizations?: OrganizationWithRole[];
  organizationUuid?: string | null;
  projectUuid?: string | null;
  flowUuid?: string | null;
  organization?: OrganizationWithRole | null;
  project?: Project | null;
  flow?: Flow | null;
};

type OptionsContextType = {
  state: OptionsState;
  dispatch: React.Dispatch<OptionsAction>;
};

export const OptionsContext = createContext<OptionsContextType>(defaultContextValue);

export const initialOptionsState: OptionsState = {
  appInfo: {},
  organizations: [],
  activeOrganization: null,
  activeProject: null,
  activeFlow: null,
};

// Loads organization from local storage if valid
const loadActiveOrganization = (organizations: OrganizationWithRole[]): OrganizationWithRole | null => {
  const defaultOrganization = organizations[0] || null;

  const lastOrganizationUuid = localStorage.getItem(STORAGE_KEYS.ORGANIZATION);
  const lastOrganization = organizations.filter((organization: OrganizationWithRole) => {
    return organization.uuid === lastOrganizationUuid;
  })[0];

  return lastOrganization || defaultOrganization;
};

// Hydrates the previously-active project from localStorage (uuid only — full
// entity is fetched on demand via useActiveProjectQuery in components).
const hydrateActiveProjectFromStorage = (): Project | null => {
  const uuid = localStorage.getItem(STORAGE_KEYS.PROJECT);
  return uuid ? ({ uuid, name: '' } as Project) : null;
};

// Hydrates the previously-active flow from localStorage. Defaults to 'all'
// when nothing is stored — the previous "last flow in the array" default no
// longer applies because we don't fetch the flows array eagerly.
const hydrateActiveFlowFromStorage = (): Flow | 'all' | null => {
  const stored = localStorage.getItem(STORAGE_KEYS.FLOW);
  if (!stored) return 'all';
  if (stored === 'all') return 'all';
  return { uuid: stored, name: '' } as Flow;
};

export const optionsReducer = (
  state: OptionsState,
  action: OptionsAction,
): OptionsState => {
  switch (action.type) {
    case 'SET_APP_INFO': {
      const appInfo = action?.appInfo as AppInfo;

      return {
        ...state,
        appInfo,
      };
    }
    case 'SET_ORGANIZATIONS': {
      const organizations = action?.organizations || [];
      const activeOrganization = loadActiveOrganization(organizations);
      localStorage.setItem(STORAGE_KEYS.ORGANIZATION, activeOrganization?.uuid || '');

      // On first organizations load, also restore active project/flow from
      // localStorage (uuid stubs — hydration queries fill in the names).
      const activeProject = state.activeProject ?? hydrateActiveProjectFromStorage();
      const activeFlow = state.activeFlow ?? hydrateActiveFlowFromStorage();

      return {
        ...state,
        organizations: organizations,
        activeOrganization: activeOrganization,
        activeProject,
        activeFlow,
      };
    }
    case 'SET_ACTIVE_ORGANIZATION': {
      const organizationUuid = action.organizationUuid;

      localStorage.setItem(STORAGE_KEYS.ORGANIZATION, organizationUuid || '');
      // Clear persisted project/flow so a reload after an org switch doesn't
      // restore selections that belong to the previous organization.
      localStorage.setItem(STORAGE_KEYS.PROJECT, '');
      localStorage.setItem(STORAGE_KEYS.FLOW, 'all');

      const organization =
        action.organization ??
        state.organizations.find(
          (organization: Organization) => organization.uuid === organizationUuid,
        ) ??
        null;

      // Reset project and flow when changing organization. Flow defaults to
      // 'all' so dashboard queries scoped by flow refetch for the new project
      // instead of staying disabled with stale data on screen.
      return {
        ...state,
        activeOrganization: organization,
        activeProject: null,
        activeFlow: 'all',
      };
    }
    case 'SET_ACTIVE_PROJECT': {
      const projectUuid = action.projectUuid;

      localStorage.setItem(STORAGE_KEYS.PROJECT, projectUuid || '');
      localStorage.setItem(STORAGE_KEYS.FLOW, 'all');

      const project = action.project ?? (projectUuid ? ({ uuid: projectUuid, name: '' } as Project) : null);

      // Default flow to 'all' (not null) when project changes so flow-scoped
      // queries remain enabled and refetch against the new project.
      return {
        ...state,
        activeProject: project,
        activeFlow: 'all',
      };
    }
    case 'SET_ACTIVE_FLOW': {
      const flowUuid = action.flowUuid;

      localStorage.setItem(STORAGE_KEYS.FLOW, flowUuid || '');

      let flow: Flow | 'all' | null = 'all';
      if (flowUuid !== 'all') {
        flow = action.flow ?? (flowUuid ? ({ uuid: flowUuid, name: '' } as Flow) : null);
      }

      return {
        ...state,
        activeFlow: flow,
      };
    }
    default:
      return state;
  }
};

export const setAppInfo = (appInfo: AppInfo) => ({
  type: 'SET_APP_INFO',
  appInfo,
});

export const setOrganizations = (organizations: OrganizationWithRole[]) => ({
  type: 'SET_ORGANIZATIONS',
  organizations,
});

export const setActiveOrganization = (
  organizationUuid: string | null,
  organization?: OrganizationWithRole | null,
) => ({
  type: 'SET_ACTIVE_ORGANIZATION',
  organizationUuid,
  organization,
});

export const setActiveProject = (
  projectUuid: string | null,
  project?: Project | null,
) => ({
  type: 'SET_ACTIVE_PROJECT',
  projectUuid,
  project,
});

export const setActiveFlow = (flowUuid: string | null, flow?: Flow | null) => ({
  type: 'SET_ACTIVE_FLOW',
  flowUuid,
  flow,
});
